// ════════════════════════════════════════════════════════════════════
// Glass-i — Proforma nesting seeded masters + type constants.
//
// Interlayer / Spacer / Gas-fill have no DB masters yet (decision: seed the
// GlassMaster lists in-app now, wire to real masters later). Glass PANES are
// NOT seeded here — they come from the real Item master (loadItemsSafe).
//
// Row TYPE is the source of truth for nesting behaviour (mirrors the HTML):
//   SHEET / LAMI / DGU / LAMI+DGU  → nesting parents (master units)
//   T / INTERLAYER / SPACER / GASFILL → generated child components
//   HARDWARE / SERVICES / ''       → plain priced lines
// ════════════════════════════════════════════════════════════════════

export const LINE_TYPE = {
  SHEET: 'SHEET',
  LAMI: 'LAMI',
  DGU: 'DGU',
  LAMI_DGU: 'LAMI+DGU',
  GLASS: 'T',
  INTERLAYER: 'INTERLAYER',
  SPACER: 'SPACER',
  GASFILL: 'GASFILL',
  HARDWARE: 'HARDWARE',
  SERVICES: 'SERVICES'
};

// A unit row that owns child component rows.
export const NEST_PARENT_TYPES = ['SHEET', 'LAMI', 'DGU', 'LAMI+DGU'];
export function isNestParentType(t) { return NEST_PARENT_TYPES.includes(String(t || '').toUpperCase()); }
export function isLamiOrDgu(t) {
  const u = String(t || '').toUpperCase();
  return u === 'LAMI' || u === 'DGU' || u === 'LAMI+DGU';
}

// Row background tints by type (mirrors the GlassMaster palette).
export function nestRowTint(type, level) {
  const u = String(type || '').toUpperCase();
  if (u === 'SHEET') return level > 0 ? '#fef3c7' : '#fde68a';        // child-sheet : master sheet
  if (u === 'LAMI' || u === 'DGU' || u === 'LAMI+DGU') return level > 0 ? '#f5f3ff' : '#ede9fe';
  if (u === 'INTERLAYER') return '#fffbeb';
  if (u === 'SPACER') return '#fffbeb';
  if (u === 'GASFILL') return '#f0f9ff';
  if (u === 'COMBO') return level > 0 ? '#ecfeff' : '#cffafe';   // combo child : combo master
  return null;
}

// ── Seeded masters (mirror the HTML) ────────────────────────────────
export const INTERLAYER_MASTER = [
  { id: 1, name: '0.38MM PVB CLEAR', thickness: 0.38 },
  { id: 2, name: '0.76MM PVB CLEAR', thickness: 0.76 },
  { id: 3, name: '1.14MM PVB CLEAR', thickness: 1.14 },
  { id: 4, name: '1.52MM PVB CLEAR', thickness: 1.52 },
  { id: 5, name: '2.28MM PVB CLEAR', thickness: 2.28 },
  { id: 6, name: '0.76MM PVB MILKY WHITE', thickness: 0.76 },
  { id: 7, name: '1.52MM PVB MILKY WHITE', thickness: 1.52 },
  { id: 8, name: '0.38MM SGP CLEAR', thickness: 0.38 },
  { id: 9, name: '0.76MM SGP CLEAR', thickness: 0.76 },
  { id: 10, name: '1.52MM SGP CLEAR', thickness: 1.52 },
  { id: 11, name: '2.28MM SGP CLEAR', thickness: 2.28 },
  { id: 12, name: '1.52MM EVA CLEAR', thickness: 1.52 },
  { id: 13, name: '0.76MM EVA CLEAR', thickness: 0.76 }
];

export const SPACER_MASTER = [
  { id: 1, name: '6MM ALUMINIUM SPACER', thickness: 6 },
  { id: 2, name: '8MM ALUMINIUM SPACER', thickness: 8 },
  { id: 3, name: '10MM ALUMINIUM SPACER', thickness: 10 },
  { id: 4, name: '12MM ALUMINIUM SPACER', thickness: 12 },
  { id: 5, name: '14MM ALUMINIUM SPACER', thickness: 14 },
  { id: 6, name: '16MM ALUMINIUM SPACER', thickness: 16 },
  { id: 7, name: '20MM ALUMINIUM SPACER', thickness: 20 },
  { id: 8, name: '6MM WARM EDGE SPACER', thickness: 6 },
  { id: 9, name: '8MM WARM EDGE SPACER', thickness: 8 },
  { id: 10, name: '10MM WARM EDGE SPACER', thickness: 10 },
  { id: 11, name: '12MM WARM EDGE SPACER', thickness: 12 },
  { id: 12, name: '16MM WARM EDGE SPACER', thickness: 16 }
];

export const GAS_FILL_OPTIONS = ['DRY AIR', 'ARGON GAS', 'KRYPTON GAS', 'XENON GAS'];
export const SEALANT_OPTIONS = ['PRIMARY + SECONDARY', 'PRIMARY ONLY', 'STRUCTURAL'];
export const DESICCANT_OPTIONS = ['MOLECULAR SIEVE', 'SILICA GEL', 'ZEOLITE', 'NONE'];

// Sum the leading "NN.NMM" thickness out of a product name.
function thicknessOf(name) {
  const m = String(name || '').match(/^([\d.]+)\s*MM/i);
  return m ? parseFloat(m[1]) : 0;
}

// ── Assembly glass-type label (Stage 6Q.2) ──────────────────────────
// A LAMI / DGU / LAMI+DGU parent's printed/grid TYPE is the glass-type code
// DERIVED from its glass panes' temper — not the raw structural line_type and
// not a static pick. Mirrors the glass-type master codes (A-LAMI / T-LAMI /
// T+A+LAMI, A-DGU / T-DGU / T+A+DGU). Single temper → "<A|T>-<base>"; a mix of
// toughened + annealed panes → "T+A+<base>".

// Temper of one glass pane: 'T' (toughened/heat-strengthened), 'A' (annealed),
// or null when it cannot be told. Prefers an explicit glass-type code, else
// reads the description keywords.
export function paneTemper(text, glassTypeCode) {
  const g = String(glassTypeCode || '').toUpperCase();
  if (g.startsWith('T')) return 'T';
  if (g.startsWith('A')) return 'A';
  const s = String(text || '').toUpperCase();
  if (/TOUGHEN|TUFF|\bTGH\b|HEAT[\s-]?STRENGTH|\bHS\b/.test(s)) return 'T';
  if (/ANNEAL/.test(s)) return 'A';
  return null;
}

// The structural base for the label, from the parent line_type.
export function assemblyBaseLabel(lineType) {
  const u = String(lineType || '').toUpperCase();
  if (u === 'LAMI') return 'LAMI';
  if (u === 'DGU') return 'DGU';
  if (u === 'LAMI+DGU') return 'LAMI+DGU';
  return null;
}

// Shape / Template display markers for an assembly, derived from its plies:
// "(S)" when ANY ply is shaped, "(T)" when ANY ply needs a template, S before T,
// "" when neither. Display-only cosmetic prefix on the TYPE label — it NEVER
// affects routing, GST, HSN, or any master lookup (those read the temper-derived
// code / per-ply snapshots). `panes` = [{ shape, template }, …].
export function assemblyMarkerPrefix(panes) {
  const arr = Array.isArray(panes) ? panes : [];
  const anyShape = arr.some((p) => p && p.shape === true);
  const anyTemplate = arr.some((p) => p && p.template === true);
  return `${anyShape ? '(S)' : ''}${anyTemplate ? '(T)' : ''}`;
}

// Derive the temper-prefixed assembly glass-type label from the glass panes.
// `panes` = [{ text, glassTypeCode, shape?, template? }]. Returns the plain base
// when no pane temper can be determined (never guesses). A leading (S)/(T) marker
// is prepended when any ply carries the Shape / Template flag (display-only).
export function assemblyTypeLabel(lineType, panes) {
  const base = assemblyBaseLabel(lineType);
  if (!base) return null;
  const arr = Array.isArray(panes) ? panes : [];
  const tempers = arr
    .map((p) => paneTemper(p && p.text, p && p.glassTypeCode))
    .filter(Boolean);
  const set = new Set(tempers);
  let core;
  if (set.size === 0) core = base;
  else {
    const temper = set.size === 1 ? [...set][0] : 'T+A';
    core = temper === 'T+A' ? `T+A+${base}` : `${temper}-${base}`;
  }
  return assemblyMarkerPrefix(arr) + core;
}

// A glass ply's narration text = its product, plus any per-ply process tags in
// parentheses (e.g. "6MM CLEAR (WITH TOUGHENED)"). Backward compatible: layers
// with no `tags` render exactly as before.
function plyText(l) {
  const product = (l && l.product) || '';
  const tags = (l && Array.isArray(l.tags)) ? l.tags.map((t) => String(t).trim().toUpperCase()).filter(Boolean) : [];
  if (!tags.length) return product;
  const suffix = tags.length === 1 ? `WITH ${tags[0]}` : `WITH ${tags.slice(0, -1).join(', ')} AND ${tags[tags.length - 1]}`;
  return `${product} (${suffix})`;
}

// "12.76MM Laminated Glass : 6MM CLEAR + 0.76MM PVB + 6MM CLEAR"
export function composeLamiDesc(layers) {
  const filled = (layers || []).filter((l) => l && l.product);
  const totalThk = filled.reduce((s, l) => s + thicknessOf(l.product), 0);
  const head = totalThk > 0 ? `${totalThk.toFixed(2)}MM Laminated Glass` : 'Laminated Glass';
  const body = filled.map((l) => plyText(l)).join(' + ');
  return body ? `${head} : ${body}` : head;
}

// "24.00MM Double Glazed Unit : 6MM CLEAR + 12MM ALUMINIUM SPACER (ARGON) + 6MM CLEAR"
export function composeDguDesc(layers, label) {
  const filled = (layers || []).filter((l) => l && (l.product || (l.lamiLayers && l.lamiLayers.length)));
  let totalThk = 0;
  const parts = [];
  for (const l of filled) {
    if (l.lamiLayers && l.lamiLayers.length) {
      const lt = l.lamiLayers.reduce((s, sl) => s + thicknessOf(sl.product), 0);
      totalThk += lt;
      parts.push(`${lt.toFixed(2)}MM Laminated (${l.lamiLayers.map((sl) => plyText(sl)).join(' + ')})`);
    } else if (String(l.layerType).toUpperCase() === 'SPACER') {
      totalThk += thicknessOf(l.product);
      parts.push(`${l.product} (${l.gasFill || 'DRY AIR'})`);
    } else {
      totalThk += thicknessOf(l.product);
      parts.push(plyText(l));
    }
  }
  const head = totalThk > 0 ? `${totalThk.toFixed(2)}MM ${label} Glazed Unit` : `${label} Glazed Unit`;
  return parts.length ? `${head} : ${parts.join(' + ')}` : head;
}
